import { useState } from 'react';
import type { ReactNode } from 'react';
import clsx from 'clsx';

import { Tag, Badge, Icon } from '@lib/ui';
import { Tooltip } from '@lib/ui';

export type PersonaFieldHelpProps = {
  tooltip: string;
  example?: string;
  suggestions?: string[];
  onSuggestionClick?: (suggestion: string) => void;
  children?: ReactNode;
  className?: string;
};

function PersonaFieldHelp({
  tooltip,
  example,
  suggestions,
  onSuggestionClick,
  children,
  className,
}: PersonaFieldHelpProps) {
  const [showExample, setShowExample] = useState(false);

  return (
    <div className={clsx('space-y-2', className)}>
      {/* Field content with tooltip */}
      <div className="flex items-start gap-2">
        <div className="flex-1">{children}</div>
        <Tooltip content={tooltip} position="top">
          <button
            type="button"
            className="mt-1 p-1 text-gray-400 hover:text-gray-600 dark:text-dark-400 dark:hover:text-dark-200 transition-colors"
          >
            <Icon name="info" size="sm" />
          </button>
        </Tooltip>
      </div>

      {/* Expandable example section */}
      {example && (
        <div>
          <button
            type="button"
            onClick={() => setShowExample(!showExample)}
            className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
          >
            <Icon
              name="chevron-down"
              size="sm"
              className={clsx('transition-transform duration-200', showExample && 'rotate-180')}
            />
            {showExample ? 'Hide example' : 'Show me an example'}
          </button>

          {showExample && (
            <div className="mt-2 p-3 bg-gray-50 dark:bg-dark-700 rounded-lg border border-gray-200 dark:border-dark-600 animate-in fade-in slide-in-from-top-1 duration-200">
              <Badge variant="soft" color="info" className="mb-2 text-xs">
                Example
              </Badge>
              <p className="text-sm text-gray-600 dark:text-dark-300 italic">"{example}"</p>
            </div>
          )}
        </div>
      )}

      {/* Suggestion chips */}
      {suggestions && suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {suggestions.map((suggestion) => (
            <Tag
              key={suggestion}
              size="sm"
              variant="soft"
              color="neutral"
              className="cursor-pointer hover:bg-gray-200 dark:hover:bg-dark-500 transition-colors"
              onClick={() => onSuggestionClick?.(suggestion)}
            >
              {suggestion}
            </Tag>
          ))}
        </div>
      )}
    </div>
  );
}

PersonaFieldHelp.displayName = 'PersonaFieldHelp';

export { PersonaFieldHelp };
